import { useState, useEffect, Fragment } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ChevronDown, ChevronRight, Plus, Printer, Info } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { CreateIssueForm } from '../../components/stores/CreateIssueForm';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const EXEC_ROLES = new Set(['super_admin', 'md', 'ceo', 'cmd', 'cfo']);
const SENIOR_PROFILES = new Set(['ceo', 'cmd', 'md', 'cfo']);
// Batch-1 fix (30-Sep-2026 Stores doc): direct "New Issue" (no MR) is only for
// super_admin/Senior Leadership — everyone else issues against an approved MR.
const canDirectIssue = (user) => EXEC_ROLES.has((user?.role || '').toLowerCase())
  || SENIOR_PROFILES.has((user?.senior_profile || '').toLowerCase());
// ADM-CR-02: Office issues (no MR concept) are also open to the Admin department.
const canOfficeIssue = (user) => (user?.department || '').toLowerCase() === 'admin';

/**
 * Material Issues Tab — direct issue (New Issue, FIFO-consumed) plus issues
 * produced by approving & issuing a Material Requisition (MR tab). `compact`
 * renders a mobile card list instead of the desktop table.
 */
export function IssuesTab({ projectId, compact = false }) {
  const { user } = useAuth();
  const canIssueDirect = canDirectIssue(user);
  const canIssueOffice = canIssueDirect || canOfficeIssue(user);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/stores/issue', projectId ? { project_id: projectId } : {});
      setIssues(data.items || []);
    } catch (err) {
      console.error('Error fetching issues:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPdf = async (issueId) => {
    try {
      const tkn = localStorage.getItem('access_token');
      const res = await fetch(`${API}/api/stores/issue/${issueId}/pdf`, { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {} });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Could not open issue slip'); }
  };

  return (
    <div className="space-y-4" data-testid="stores-issues-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-gray-500">{issues.length} Material Issues</div>
        <div className="flex items-center gap-3">
          {!compact && <div className="text-xs text-slate-500" data-testid="issue-flow-hint">
            {canIssueDirect ? 'Raise directly here, or approve & issue a Material Requisition (MR tab).'
              : canIssueOffice ? 'Issue Office supplies directly, or issue project material against an approved MR (MR tab).'
              : 'Issue against an approved Material Requisition (MR tab).'}
          </div>}
          {canIssueOffice && (
            <Button onClick={() => setShowCreate(v => !v)} data-testid="new-issue-btn">
              <Plus className="w-4 h-4 mr-2" /> New Issue
            </Button>
          )}
        </div>
      </div>
      {!compact && !canIssueDirect && (
        <div className="bg-sky-50 border border-sky-200 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="issue-guidance-panel">
          <Info size={16} className="text-sky-600 mt-0.5 shrink-0" />
          <div className="text-xs text-sky-900 space-y-2">
            <div className="font-semibold">Material is issued against an approved Material Requisition.</div>
            <div>Open the <strong>MR tab</strong>, pick an approved MR and click Issue. For Central Godown stock, raise the MR with <strong>Source = Central Godown</strong>; the issue unlocks once the site receives the transfer.</div>
            <button
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('tab', 'mr');
                window.history.pushState({}, '', url.toString());
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-700 text-white rounded text-[11px] font-semibold hover:bg-sky-800"
              data-testid="issue-go-to-mr-btn">
              Go to MR tab →
            </button>
          </div>
        </div>
      )}
      {showCreate && canIssueOffice && (
        <CreateIssueForm projectId={projectId} compact={compact} restrictToOffice={!canIssueDirect}
          onCancel={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {compact ? (
        <div className="space-y-2" data-testid="issues-card-list">
          {loading ? <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>
            : issues.length === 0 ? <div className="text-center py-6 text-slate-400 text-sm">No material issues found</div>
            : issues.map(issue => (
              <div key={issue.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`issue-card-${issue.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{issue.issue_no}</span>
                  <span className="text-xs font-semibold text-slate-600">₹{(issue.total_value || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">{issue.location_type === 'office' ? issue.office_location_name : issue.project_name} · {issue.issued_to}</div>
                <div className="text-[11px] text-slate-500 mt-1.5 space-y-0.5">
                  {(issue.items || []).map((it, i) => (
                    <div key={i}>{it.description} — {it.issued_qty} {it.unit}</div>
                  ))}
                </div>
                <div className="text-[11px] text-slate-400 mt-1.5">{formatDate(issue.created_at)}</div>
              </div>
            ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full text-sm sticky-col-header">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Issue Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Issued To</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Items</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Value</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                  <th className="px-4 py-3"></th>
                </tr></thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
                  ) : issues.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-500">No material issues found</td></tr>
                  ) : issues.map(issue => {
                    const isOpen = !!expanded[issue.id];
                    const hasLegacy = (issue.items || []).some(it => it.has_legacy_stock);
                    return (
                      <Fragment key={issue.id}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [issue.id]: !e[issue.id] }))} data-testid={`issue-row-${issue.id}`}>
                          <td className="px-4 py-3 font-mono font-medium">
                            <span className="inline-flex items-center gap-1">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                              {issue.issue_no}
                            </span>
                            {hasLegacy && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium" data-testid={`issue-legacy-${issue.id}`}>Legacy stock</span>}
                          </td>
                          <td className="px-4 py-3">{issue.location_type === 'office' ? issue.office_location_name : issue.project_name}</td>
                          <td className="px-4 py-3">{issue.issued_to}</td>
                          <td className="px-4 py-3 text-right">{issue.items?.length || 0}</td>
                          <td className="px-4 py-3 text-right font-medium">₹{issue.total_value?.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{formatDate(issue.created_at)}</td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={(e) => { e.stopPropagation(); openPdf(issue.id); }}
                              title="Print Issue Slip" data-testid={`issue-pdf-${issue.id}`}
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-[#3b4872] inline-flex">
                              <Printer size={14} />
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`issue-detail-${issue.id}`}>
                            <td colSpan={7} className="bg-gray-50 px-6 py-3">
                              {issue.fifo_note && <div className="text-xs text-amber-700 mb-2">⚠ {issue.fifo_note}</div>}
                              <table className="w-full text-xs">
                                <thead><tr className="text-left text-gray-500 border-b">
                                  <th className="py-1">Item</th><th className="text-right">Issued</th><th className="text-right">FIFO rate</th><th className="text-right">Cost</th><th className="pl-3">FIFO breakdown (batch · qty · rate)</th>
                                </tr></thead>
                                <tbody>
                                  {(issue.items || []).map((it, i) => (
                                    <tr key={i} className="border-b align-top">
                                      <td className="py-1.5">{it.description}{it.has_legacy_stock && <span className="ml-1 text-amber-600">•</span>}</td>
                                      <td className="text-right">{it.issued_qty} {it.unit}</td>
                                      <td className="text-right font-medium">₹{(it.rate || 0).toLocaleString('en-IN')}</td>
                                      <td className="text-right font-medium">₹{(it.amount || 0).toLocaleString('en-IN')}</td>
                                      <td className="pl-3">
                                        {(it.fifo_layers || []).length === 0 ? <span className="text-gray-400">—</span> :
                                          (it.fifo_layers || []).map((fl, j) => (
                                            <span key={j} className={`inline-block mr-2 mb-1 px-1.5 py-0.5 rounded ${fl.layer_id === 'legacy' ? 'bg-amber-100 text-amber-700' : fl.is_return ? 'bg-purple-100 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                                              {fl.grn_no} · {fl.qty_consumed} @ ₹{fl.rate}
                                            </span>
                                          ))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default IssuesTab;
